import { Component, ErrorInfo, ReactNode } from 'react';

import * as amplitude from '@amplitude/analytics-browser';

import ErrorFallbackComponent from './ErrorFallback';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary component that catches JavaScript errors in its child component tree,
 * logs those errors, and displays a fallback UI instead of crashing the whole app.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to console
    console.error('Error caught by ErrorBoundary:', error, errorInfo);

    // Log to Amplitude if available
    try {
      amplitude.logEvent('Error_Boundary_Caught', {
        error: error.toString(),
        componentStack: errorInfo.componentStack,
        location: window.location.href,
      });
    } catch (loggingError) {
      console.error('Failed to log error to analytics:', loggingError);
    }

    this.setState({
      errorInfo,
    });

    // Note: React's development error overlay will still appear in development mode
    // This is expected behavior and won't happen in production
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      // Use the custom ErrorFallback component if no fallback is provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Use our custom ErrorFallbackComponent with the error details
      return (
        <ErrorFallbackComponent
          error={this.state.error}
          resetErrorBoundary={() => {
            this.setState({ hasError: false, error: null, errorInfo: null });
          }}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
